import { useState, useEffect } from 'react';
import { Loader2, Save, Settings, Truck, CreditCard, Percent, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin/shop';

export default function AdminShopSettingsPage() {
  const [settings, setSettings] = useState({
    shipping_charge: 50,
    free_shipping_threshold: 500,
    tax_percentage: 18,
    cod_enabled: true,
    online_payment_enabled: false,
    currency: 'INR',
    return_policy: '',
    cancellation_policy: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_admin_token')}` }
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, getAuthHeaders());
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Shop Settings</h1>
          <p className="text-slate-500 text-sm mt-1">Configure DharmShop settings</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Shipping */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Shipping</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Shipping Charge (₹)</label>
              <input type="number" value={settings.shipping_charge}
                onChange={(e) => setSettings({...settings, shipping_charge: parseFloat(e.target.value) || 0})}
                min="0" className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Free Shipping Threshold (₹)</label>
              <input type="number" value={settings.free_shipping_threshold}
                onChange={(e) => setSettings({...settings, free_shipping_threshold: parseFloat(e.target.value) || 0})}
                min="0" className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
              <p className="text-xs text-slate-500 mt-1">Orders above this amount get free shipping</p>
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Payment</h2>
          </div>
          <div className="space-y-4">
            <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer">
              <div>
                <p className="font-medium text-slate-900">Cash on Delivery</p>
                <p className="text-xs text-slate-500">Allow customers to pay on delivery</p>
              </div>
              <input type="checkbox" checked={settings.cod_enabled}
                onChange={(e) => setSettings({...settings, cod_enabled: e.target.checked})}
                className="w-5 h-5 text-amber-500 rounded" />
            </label>
            <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer">
              <div>
                <p className="font-medium text-slate-900">Online Payment</p>
                <p className="text-xs text-slate-500">Accept cards, UPI, net banking</p>
              </div>
              <input type="checkbox" checked={settings.online_payment_enabled}
                onChange={(e) => setSettings({...settings, online_payment_enabled: e.target.checked})}
                className="w-5 h-5 text-amber-500 rounded" />
            </label>
          </div>
        </div>

        {/* Tax */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Percent className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Tax</h2>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">GST Percentage (%)</label>
            <input type="number" value={settings.tax_percentage}
              onChange={(e) => setSettings({...settings, tax_percentage: parseFloat(e.target.value) || 0})}
              min="0" max="100" className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
            <p className="text-xs text-slate-500 mt-1">Applied to all products</p>
          </div>
        </div>

        {/* Currency */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">General</h2>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
            <select value={settings.currency}
              onChange={(e) => setSettings({...settings, currency: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg">
              <option value="INR">Indian Rupee (₹)</option>
              <option value="USD">US Dollar ($)</option>
            </select>
          </div>
        </div>

        {/* Policies */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Policies</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Return Policy</label>
              <textarea value={settings.return_policy}
                onChange={(e) => setSettings({...settings, return_policy: e.target.value})}
                rows={4} className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none"
                placeholder="Enter return policy..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cancellation Policy</label>
              <textarea value={settings.cancellation_policy}
                onChange={(e) => setSettings({...settings, cancellation_policy: e.target.value})}
                rows={4} className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none"
                placeholder="Enter cancellation policy..." />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
